import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Input,
  VStack,
  Heading,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  Code,
  Badge,
  Flex,
  Spacer,
  Checkbox,
  Textarea,
  Divider,
  HStack,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';
import { useGlobalAction, useUser } from '@gadgetinc/react';
import { api } from '../api';
import { useNavigate } from 'react-router-dom';

export default function FisdapTest() {
  const navigate = useNavigate();
  const [shiftId, setShiftId] = useState('');
  const [bypassMode, setBypassMode] = useState(true);
  const [mockSubject, setMockSubject] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [testError, setTestError] = useState(null);
  const [evalResults, setEvalResults] = useState(null);
  const [evalTestError, setEvalTestError] = useState(null);
  const [showEvalResults, setShowEvalResults] = useState(false);
  const [aiShiftId, setAiShiftId] = useState('');
  const [autoSendToAI, setAutoSendToAI] = useState(false);
  const [structuredDataPreview, setStructuredDataPreview] = useState(null);

  const [{ data, error, fetching }, testFisdapScraping] = useGlobalAction(
    api.testFisdapScraping
  );

  const [{ data: evalData, error: evalError, fetching: evalFetching }, evaluateShift] = useGlobalAction(
    api.evaluateShift
  );

  const user = useUser();

  // Handle action completion
  useEffect(() => {
    if (data) {
      setResults(data);
      setIsModalOpen(false);
      
      // Set structured data preview
      if (data.structured_data) {
        setStructuredDataPreview(data.structured_data);
      }
      
      // Auto-send to AI if toggle is on and we have aiShiftId
      if (autoSendToAI && aiShiftId.trim()) {
        handleTestEvaluation();
      }
    }
    if (error) {
      setTestError(error);
      setIsModalOpen(false);
    }
  }, [data, error, autoSendToAI, aiShiftId]);

  // Handle AI evaluation completion
  useEffect(() => {
    if (evalData) {
      setEvalResults(evalData);
      setShowEvalResults(true);
    }
    if (evalError) {
      setEvalTestError(evalError);
    }
  }, [evalData, evalError]);

  const handleTestScrape = async () => {
    if (!shiftId.trim()) {
      setTestError(new Error('Please enter a shift ID'));
      return;
    }

    setTestError(null);
    setResults(null);
    setIsModalOpen(true);

    try {
      await testFisdapScraping({
        shiftId: shiftId.trim(),
        bypassMode: bypassMode,
        mockSubject: mockSubject.trim() || undefined
      });
    } catch (err) {
      setTestError(err);
      setIsModalOpen(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleTestEvaluation = async () => {
    const targetShiftId = aiShiftId.trim();
    if (!targetShiftId) {
      setEvalTestError(new Error('Please enter a shift ID for AI evaluation.'));
      return;
    }

    setEvalTestError(null);
    setEvalResults(null);
    setShowEvalResults(false);

    try {
      // Call evaluateShift with just the shiftId - let it run its normal course like a webhook
      await evaluateShift({
        shiftId: targetShiftId,
        userId: user?.id || '1'
      });
    } catch (err) {
      setEvalTestError(err);
    }
  };

  const resetTest = () => {
    setResults(null);
    setTestError(null);
    setShiftId('');
    setMockSubject('');
    setStructuredDataPreview(null);
    setEvalResults(null);
    setEvalTestError(null);
    setShowEvalResults(false);
  };

  return (
    <Box p={6} maxW="800px" mx="auto">
      <Flex mb={6} align="center">
        <Heading size="lg" color="blue.600">
          FISDAP Scraping Test
        </Heading>
        <Spacer />
        <Button 
          variant="outline" 
          onClick={() => navigate('/admin')}
          size="sm"
        >
          Back to Admin
        </Button>
      </Flex>

      <Card mb={6}>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {/* Mode Selection */}
            <Box>
              <Checkbox
                isChecked={bypassMode}
                onChange={(e) => setBypassMode(e.target.checked)}
                colorScheme="blue"
                size="lg"
                isDisabled={fetching}
              >
                <VStack align="start" spacing={1} ml={2}>
                  <Text fontWeight="medium">
                    Test Mode (Bypass Database)
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    When enabled, parsing results are returned without saving to database
                  </Text>
                </VStack>
              </Checkbox>
              
              <Box mt={3} p={3} bg={bypassMode ? "green.50" : "orange.50"} borderRadius="md" borderLeft="4px" borderLeftColor={bypassMode ? "green.400" : "orange.400"}>
                <Text fontSize="sm" color={bypassMode ? "green.700" : "orange.700"}>
                  <strong>{bypassMode ? "Test Mode:" : "Full Mode:"}</strong> {bypassMode 
                    ? "Data will be parsed and returned for validation. No database records will be created."
                    : "Data will be parsed AND saved to the database. Patient encounters and related records will be created."
                  }
                </Text>
              </Box>
            </Box>

            <Divider />

            {/* Mock Subject Input */}
            <Box>
              <Text mb={2} fontWeight="medium">
                Mock Email Subject (Optional)
              </Text>
              <Textarea
                placeholder="Enter mock email subject to test Team Lead and other subject-based logic (e.g., 'TL John Smith - Shift 12345')"
                value={mockSubject}
                onChange={(e) => setMockSubject(e.target.value)}
                size="md"
                isDisabled={fetching}
                rows={2}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Used to test Team Lead (TL) detection and other subject line parsing logic
              </Text>
            </Box>

            {/* Shift ID Input */}
            <Box>
              <Text mb={2} fontWeight="medium">
                FISDAP Shift ID*
              </Text>
              <Input
                placeholder="Enter FISDAP shift ID to test scraping"
                value={shiftId}
                onChange={(e) => setShiftId(e.target.value)}
                size="lg"
                isDisabled={fetching}
              />
            </Box>

            <Button
              colorScheme="blue"
              size="lg"
              onClick={handleTestScrape}
              isLoading={fetching}
              loadingText="Scraping..."
              isDisabled={!shiftId.trim()}
            >
              {bypassMode ? "Test Scrape (No Save)" : "Full Scrape & Save"}
            </Button>
          </VStack>
        </CardBody>
      </Card>

      {/* Enhanced Results Section */}
      {results && (
        <Card mb={6}>
          <CardBody>
            <Flex align="center" mb={4}>
              <Heading size="md" color="green.600">
                Scraping Results
              </Heading>
              <Spacer />
              <HStack spacing={2}>
                <Badge colorScheme={bypassMode ? "blue" : "green"} variant="subtle">
                  {bypassMode ? "Test Mode" : "Full Mode"}
                </Badge>
                <Badge colorScheme="green" variant="subtle">
                  Success
                </Badge>
              </HStack>
            </Flex>
            
            <VStack spacing={6} align="stretch">
              {results.success && (
                <Alert status="success">
                  <AlertIcon />
                  FISDAP data {bypassMode ? "extracted and parsed" : "extracted, parsed, and saved"} successfully!
                </Alert>
              )}

              {/* Timing Information */}
              {results.duration && (
                <Box p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" color="blue.700">
                    <strong>Processing completed in:</strong> {results.duration}ms
                  </Text>
                </Box>
              )}

              {/* Parsed FISDAP Data Structure */}
              {results.parsedData && (
                <Accordion defaultIndex={[0]} allowMultiple>
                  {/* Shift Information */}
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="bold" color="blue.700">Shift Information</Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {results.parsedData.shiftId && (
                          <Box p={3} bg="gray.50" borderRadius="md">
                            <Text fontWeight="medium" fontSize="sm" color="gray.600">Shift ID</Text>
                            <Text>{results.parsedData.shiftId}</Text>
                          </Box>
                        )}
                        {results.parsedData.shiftLocked !== undefined && (
                          <Box p={3} bg="gray.50" borderRadius="md">
                            <Text fontWeight="medium" fontSize="sm" color="gray.600">Locked Status</Text>
                            <Badge colorScheme={results.parsedData.shiftLocked ? "red" : "green"}>
                              {results.parsedData.shiftLocked ? "Locked" : "Unlocked"}
                            </Badge>
                          </Box>
                        )}
                        {results.parsedData.location && (
                          <Box p={3} bg="gray.50" borderRadius="md">
                            <Text fontWeight="medium" fontSize="sm" color="gray.600">Location</Text>
                            <Text>{results.parsedData.location}</Text>
                          </Box>
                        )}
                        {results.parsedData.preceptorName && (
                          <Box p={3} bg="gray.50" borderRadius="md">
                            <Text fontWeight="medium" fontSize="sm" color="gray.600">Preceptor</Text>
                            <Text>{results.parsedData.preceptorName}</Text>
                          </Box>
                        )}
                        {results.parsedData.teamLeadFinal !== undefined && (
                          <Box p={3} bg="gray.50" borderRadius="md">
                            <Text fontWeight="medium" fontSize="sm" color="gray.600">Team Lead Status</Text>
                            <Badge colorScheme={results.parsedData.teamLeadFinal ? "purple" : "gray"}>
                              {results.parsedData.teamLeadFinal ? "Team Lead Final" : "Regular"}
                            </Badge>
                          </Box>
                        )}
                        {mockSubject && (
                          <Box p={3} bg="yellow.50" borderRadius="md">
                            <Text fontWeight="medium" fontSize="sm" color="yellow.700">Mock Subject Used</Text>
                            <Text fontSize="sm">{mockSubject}</Text>
                          </Box>
                        )}
                      </SimpleGrid>
                    </AccordionPanel>
                  </AccordionItem>

                  {/* Patient Count & Demographics */}
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="bold" color="green.700">
                          Patient Information
                          {results.parsedData.patientCount && (
                            <Badge ml={2} colorScheme="green">{results.parsedData.patientCount} patients</Badge>
                          )}
                        </Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <VStack spacing={4} align="stretch">
                        {results.parsedData.patientCount && (
                          <Box p={3} bg="green.50" borderRadius="md">
                            <Text fontWeight="medium" color="green.800">
                              Total Patients: {results.parsedData.patientCount}
                            </Text>
                          </Box>
                        )}
                        
                        {results.parsedData.patients && results.parsedData.patients.length > 0 && (
                          <Box>
                            <Text fontWeight="medium" mb={3}>Patient Demographics:</Text>
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                              {results.parsedData.patients.map((patient, index) => (
                                <Box key={index} p={3} bg="gray.50" borderRadius="md" borderLeft="4px" borderLeftColor="blue.400">
                                  <Text fontWeight="medium" fontSize="sm">Patient {index + 1}</Text>
                                  {patient.age && <Text fontSize="sm">Age: {patient.age}</Text>}
                                  {patient.gender && <Text fontSize="sm">Gender: {patient.gender}</Text>}
                                  {patient.chiefComplaint && <Text fontSize="sm">Chief Complaint: {patient.chiefComplaint}</Text>}
                                </Box>
                              ))}
                            </SimpleGrid>
                          </Box>
                        )}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>

                  {/* Assessment Checkboxes */}
                  {results.parsedData.checkboxData && (
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="bold" color="purple.700">Assessment Checkboxes</Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={2}>
                          {Object.entries(results.parsedData.checkboxData).map(([key, value]) => (
                            <Box key={key} p={2} bg={value ? "green.50" : "gray.50"} borderRadius="md">
                              <Text fontSize="sm" fontWeight="medium" color={value ? "green.800" : "gray.600"}>
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                              </Text>
                              <Badge size="sm" colorScheme={value ? "green" : "gray"}>
                                {value ? "✓" : "✗"}
                              </Badge>
                            </Box>
                          ))}
                        </SimpleGrid>
                      </AccordionPanel>
                    </AccordionItem>
                  )}

                  {/* Vitals Data */}
                  {results.parsedData.vitals && (
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="bold" color="red.700">Vitals Data</Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={3}>
                          {Object.entries(results.parsedData.vitals).map(([key, value]) => (
                            <Box key={key} p={3} bg="red.50" borderRadius="md">
                              <Text fontWeight="medium" fontSize="sm" color="red.600">
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                              </Text>
                              <Text fontSize="lg" fontWeight="bold">{value || "N/A"}</Text>
                            </Box>
                          ))}
                        </SimpleGrid>
                      </AccordionPanel>
                    </AccordionItem>
                  )}

                  {/* Narrative Fields */}
                  {results.parsedData.narratives && (
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="bold" color="teal.700">Narrative Fields</Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <VStack spacing={4} align="stretch">
                          {Object.entries(results.parsedData.narratives).map(([key, value]) => (
                            <Box key={key} p={3} bg="teal.50" borderRadius="md">
                              <Text fontWeight="medium" fontSize="sm" color="teal.600" mb={1}>
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                              </Text>
                              <Text fontSize="sm" whiteSpace="pre-wrap">
                                {value || "No data provided"}
                              </Text>
                            </Box>
                          ))}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  )}

                  {/* Raw HTML Preview */}
                  {results.parsedData.pdfContents && (
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <Text fontWeight="bold" color="gray.700">Raw PDF HTML Content</Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <Code 
                          p={4} 
                          borderRadius="md" 
                          display="block" 
                          whiteSpace="pre-wrap"
                          maxH="400px"
                          overflowY="auto"
                          fontSize="xs"
                          bg="gray.800"
                          color="gray.100"
                        >
                          {results.parsedData.pdfContents.substring(0, 3000)}
                          {results.parsedData.pdfContents.length > 3000 && '\n\n... (truncated)'}
                        </Code>
                        {results.parsedData.pdfContents.length > 3000 && (
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Showing first 3,000 characters of {results.parsedData.pdfContents.length.toLocaleString()} total
                          </Text>
                        )}
                      </AccordionPanel>
                    </AccordionItem>
                  )}

                  {/* Complete Raw Response */}
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="bold" color="gray.600">Complete Response Data</Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <Code 
                        p={4} 
                        borderRadius="md" 
                        display="block" 
                        whiteSpace="pre-wrap"
                        maxH="500px"
                        overflowY="auto"
                        fontSize="xs"
                      >
                        {JSON.stringify(results, null, 2)}
                      </Code>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}

              {/* If no parsed data, show raw results */}
              {!results.parsedData && results.data && (
                <Box>
                  <Text fontWeight="medium" mb={2} color="gray.700">
                    Raw Response Data:
                  </Text>
                  <Code 
                    p={4} 
                    borderRadius="md" 
                    display="block" 
                    whiteSpace="pre-wrap"
                    maxH="400px"
                    overflowY="auto"
                    fontSize="xs"
                  >
                    {JSON.stringify(results.data, null, 2)}
                  </Code>
                </Box>
              )}

              <Button onClick={resetTest} variant="outline" size="lg">
                Run Another Test
              </Button>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* AI Evaluation Section */}
      <Card mb={6}>
        <CardBody>
          <Heading size="md" color="purple.600" mb={4}>
            AI Shift Evaluation
          </Heading>
          <VStack spacing={6} align="stretch">
            <Alert status="info">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Test AI Evaluation System</Text>
                <Text fontSize="sm">Run the holistic AI evaluation system on any processed shift to see how it scores and provides feedback.</Text>
              </Box>
            </Alert>

            {/* AI Evaluation Controls */}
            <VStack spacing={4} align="stretch">
              {/* Auto-send toggle */}
              <Box>
                <Checkbox
                  isChecked={autoSendToAI}
                  onChange={(e) => setAutoSendToAI(e.target.checked)}
                  colorScheme="purple"
                  size="md"
                  isDisabled={evalFetching}
                >
                  <VStack align="start" spacing={1} ml={2}>
                    <Text fontWeight="medium">
                      Auto-send to AI after scraping
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      When enabled, automatically runs AI evaluation after FISDAP scraping completes
                    </Text>
                  </VStack>
                </Checkbox>
              </Box>

              <Divider />

              {/* AI Shift ID Input */}
              <Box>
                <Text mb={2} fontWeight="medium">
                  Shift ID for AI Evaluation*
                </Text>
                <Input
                  placeholder="Enter shift ID to evaluate with AI (can be different from scraping)"
                  value={aiShiftId}
                  onChange={(e) => setAiShiftId(e.target.value)}
                  size="lg"
                  isDisabled={evalFetching}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  This can be any processed shift ID - doesn't need to match the scraping test above
                </Text>
              </Box>

              {/* Structured Data Preview */}
              {structuredDataPreview && (
                <Box>
                  <Text fontWeight="medium" mb={3} color="purple.700">
                    Structured Data Preview (from last scrape):
                  </Text>
                  <Box p={4} bg="purple.50" borderRadius="md" borderLeft="4px" borderLeftColor="purple.400">
                    <VStack spacing={3} align="stretch">
                      {/* Shift Summary */}
                      <Box>
                        <Text fontWeight="medium" fontSize="sm" color="purple.800">Shift Summary:</Text>
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mt={2}>
                          <Box>
                            <Text fontSize="xs" color="gray.600">Shift ID</Text>
                            <Text fontSize="sm" fontWeight="medium">{structuredDataPreview.shift?.shiftId || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.600">Patients</Text>
                            <Text fontSize="sm" fontWeight="medium">{structuredDataPreview.patients?.length || 0}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.600">Location</Text>
                            <Text fontSize="sm" fontWeight="medium">{structuredDataPreview.shift?.location || 'N/A'}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.600">Team Lead</Text>
                            <Badge size="sm" colorScheme={structuredDataPreview.shift?.teamLeadFinal ? "purple" : "gray"}>
                              {structuredDataPreview.shift?.teamLeadFinal ? "Yes" : "No"}
                            </Badge>
                          </Box>
                        </SimpleGrid>
                      </Box>
                      
                      {/* Patient Preview */}
                      {structuredDataPreview.patients?.length > 0 && (
                        <Box>
                          <Text fontWeight="medium" fontSize="sm" color="purple.800">Sample Patients:</Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mt={2}>
                            {structuredDataPreview.patients.slice(0, 4).map((patient, idx) => (
                              <Box key={idx} p={2} bg="white" borderRadius="md" fontSize="xs">
                                <Text fontWeight="medium">Patient {idx + 1}</Text>
                                <Text>Age: {patient.age || 'N/A'} | Gender: {patient.gender || 'N/A'}</Text>
                                {patient.chiefComplaint && <Text>CC: {patient.chiefComplaint.substring(0, 30)}...</Text>}
                              </Box>
                            ))}
                          </SimpleGrid>
                          {structuredDataPreview.patients.length > 4 && (
                            <Text fontSize="xs" color="gray.600" mt={1}>
                              ... and {structuredDataPreview.patients.length - 4} more patients
                            </Text>
                          )}
                        </Box>
                      )}
                      
                      {/* Raw JSON Toggle */}
                      <Accordion allowToggle size="sm">
                        <AccordionItem border="none">
                          <AccordionButton p={0} _hover={{ bg: 'transparent' }}>
                            <Text fontSize="sm" color="purple.700">View Raw JSON Data</Text>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel p={0} pt={2}>
                            <Code 
                              p={3} 
                              borderRadius="md" 
                              display="block" 
                              whiteSpace="pre-wrap"
                              maxH="200px"
                              overflowY="auto"
                              fontSize="xs"
                              bg="white"
                            >
                              {JSON.stringify(structuredDataPreview, null, 2)}
                            </Code>
                          </AccordionPanel>
                        </AccordionItem>
                      </Accordion>
                    </VStack>
                  </Box>
                </Box>
              )}

              <Button
                colorScheme="purple"
                size="lg"
                onClick={handleTestEvaluation}
                isLoading={evalFetching}
                loadingText="Running AI Evaluation..."
                leftIcon={<Text>🤖</Text>}
                isDisabled={!aiShiftId.trim()}
              >
                Send to AI Evaluation
              </Button>
            </VStack>
          </VStack>
        </CardBody>
      </Card>

      {/* AI Evaluation Results */}
      {evalResults && showEvalResults && (
        <Card mb={6}>
          <CardBody>
            <Flex align="center" mb={4}>
              <Heading size="md" color="purple.600">
                AI Evaluation Results
              </Heading>
              <Spacer />
              <HStack spacing={2}>
                <Badge colorScheme={evalResults.valid ? "green" : "red"}>
                  {evalResults.valid ? "Valid" : "Invalid"}
                </Badge>
                <Badge colorScheme={evalResults.shift_pass_status === 'pass' ? "green" : evalResults.shift_pass_status === 'fail' ? "red" : "yellow"}>
                  {evalResults.shift_pass_status?.toUpperCase() || 'N/A'}
                </Badge>
                {evalResults.overall_quality_score && (
                  <Badge colorScheme="blue">
                    Quality: {evalResults.overall_quality_score}/100
                  </Badge>
                )}
              </HStack>
            </Flex>
            
            <Accordion defaultIndex={[0]} allowMultiple>
              {/* Student Report */}
              {evalResults.student_report && (
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="bold" color="blue.700">Student/Instructor Report</Text>
                      <Text fontSize="sm" color="gray.600">Clean feedback for educational use</Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {/* Shift Overview */}
                    {evalResults.student_report.shift_overview && (
                      <Box mb={6}>
                        <Text fontWeight="bold" mb={3} color="blue.800">Shift Overview</Text>
                        <VStack spacing={4} align="stretch">
                          <Box p={4} bg="blue.50" borderRadius="md">
                            <Text fontWeight="medium" mb={2}>Overall Impression:</Text>
                            <Text>{evalResults.student_report.shift_overview.overall_impression}</Text>
                          </Box>
                          
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <Box p={3} bg="gray.50" borderRadius="md">
                              <Text fontWeight="medium" fontSize="sm" color="gray.600">Pass/Fail Status</Text>
                              <Badge size="lg" colorScheme={evalResults.student_report.shift_overview.pass_fail_status === 'PASS' ? "green" : "red"}>
                                {evalResults.student_report.shift_overview.pass_fail_status}
                              </Badge>
                            </Box>
                            <Box p={3} bg="gray.50" borderRadius="md">
                              <Text fontWeight="medium" fontSize="sm" color="gray.600">Quality Assessment</Text>
                              <Text>{evalResults.student_report.shift_overview.shift_quality_assessment}</Text>
                            </Box>
                          </SimpleGrid>
                          
                          {evalResults.student_report.shift_overview.hard_failures_identified?.length > 0 && (
                            <Box p={4} bg="red.50" borderRadius="md">
                              <Text fontWeight="medium" mb={2} color="red.800">Hard Failures Identified:</Text>
                              <VStack align="start" spacing={1}>
                                {evalResults.student_report.shift_overview.hard_failures_identified.map((failure, idx) => (
                                  <Text key={idx} fontSize="sm">• {failure}</Text>
                                ))}
                              </VStack>
                            </Box>
                          )}
                          
                          {evalResults.student_report.shift_overview.general_recommendations?.length > 0 && (
                            <Box p={4} bg="green.50" borderRadius="md">
                              <Text fontWeight="medium" mb={2} color="green.800">General Recommendations:</Text>
                              <VStack align="start" spacing={1}>
                                {evalResults.student_report.shift_overview.general_recommendations.map((rec, idx) => (
                                  <Text key={idx} fontSize="sm">• {rec}</Text>
                                ))}
                              </VStack>
                            </Box>
                          )}
                        </VStack>
                      </Box>
                    )}
                    
                    {/* Patient-Specific Feedback */}
                    {evalResults.student_report.patient_specific_feedback?.length > 0 && (
                      <Box>
                        <Text fontWeight="bold" mb={3} color="green.800">Patient-Specific Feedback</Text>
                        <VStack spacing={4} align="stretch">
                          {evalResults.student_report.patient_specific_feedback.map((patient, idx) => (
                            <Box key={idx} p={4} bg="green.50" borderRadius="md" borderLeft="4px" borderLeftColor="green.400">
                              <Text fontWeight="medium" mb={2}>Patient {patient.patient_id}</Text>
                              <Text mb={3} fontSize="sm">{patient.clinical_summary}</Text>
                              
                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                {patient.key_teaching_points?.length > 0 && (
                                  <Box>
                                    <Text fontWeight="medium" fontSize="sm" mb={1}>Teaching Points:</Text>
                                    <VStack align="start" spacing={1}>
                                      {patient.key_teaching_points.map((point, pidx) => (
                                        <Text key={pidx} fontSize="xs">• {point}</Text>
                                      ))}
                                    </VStack>
                                  </Box>
                                )}
                                
                                {patient.specific_recommendations?.length > 0 && (
                                  <Box>
                                    <Text fontWeight="medium" fontSize="sm" mb={1}>Recommendations:</Text>
                                    <VStack align="start" spacing={1}>
                                      {patient.specific_recommendations.map((rec, pidx) => (
                                        <Text key={pidx} fontSize="xs">• {rec}</Text>
                                      ))}
                                    </VStack>
                                  </Box>
                                )}
                              </SimpleGrid>
                              
                              {patient.follow_up_questions?.length > 0 && (
                                <Box mt={3}>
                                  <Text fontWeight="medium" fontSize="sm" mb={1}>Follow-up Questions:</Text>
                                  <VStack align="start" spacing={1}>
                                    {patient.follow_up_questions.map((question, pidx) => (
                                      <Text key={pidx} fontSize="xs" fontStyle="italic">• {question}</Text>
                                    ))}
                                  </VStack>
                                </Box>
                              )}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              )}
              
              {/* Internal Analytics */}
              {evalResults.internal_analytics && (
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <Text fontWeight="bold" color="orange.700">Internal Analytics</Text>
                      <Text fontSize="sm" color="gray.600">Detailed scoring data for program assessment</Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    {/* Hard Criteria Results */}
                    {evalResults.internal_analytics.hard_criteria_results && (
                      <Box mb={4}>
                        <Text fontWeight="bold" mb={3}>Hard Criteria Results:</Text>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                          {Object.entries(evalResults.internal_analytics.hard_criteria_results).map(([key, value]) => (
                            <Box key={key} p={3} bg={value ? "green.50" : "red.50"} borderRadius="md">
                              <Text fontSize="sm" fontWeight="medium">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Text>
                              <Badge colorScheme={value ? "green" : "red"}>
                                {value ? "✓ Pass" : "✗ Fail"}
                              </Badge>
                            </Box>
                          ))}
                        </SimpleGrid>
                      </Box>
                    )}
                    
                    {/* Detailed Field Scores Preview */}
                    {evalResults.internal_analytics.detailed_field_scores?.length > 0 && (
                      <Box>
                        <Text fontWeight="bold" mb={3}>Field Scoring Summary:</Text>
                        <Text fontSize="sm" color="gray.600" mb={3}>
                          Detailed per-patient, per-field scoring data available. Showing summary for {evalResults.internal_analytics.detailed_field_scores.length} patients.
                        </Text>
                        <Code p={4} borderRadius="md" display="block" whiteSpace="pre-wrap" fontSize="xs" maxH="300px" overflowY="auto">
                          {JSON.stringify(evalResults.internal_analytics.detailed_field_scores, null, 2)}
                        </Code>
                      </Box>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              )}
              
              {/* Complete Raw AI Response */}
              <AccordionItem>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    <Text fontWeight="bold" color="gray.600">Complete AI Response</Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Code p={4} borderRadius="md" display="block" whiteSpace="pre-wrap" fontSize="xs" maxH="500px" overflowY="auto">
                    {JSON.stringify(evalResults, null, 2)}
                  </Code>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </CardBody>
        </Card>
      )}

      {/* Enhanced Error Section */}
      {(testError || evalTestError) && (
        <Card mb={6} borderColor="red.200">
          <CardBody>
            <Flex align="center" mb={4}>
              <Heading size="md" color="red.600">
                {testError ? "Scraping Failed" : "AI Evaluation Failed"}
              </Heading>
              <Spacer />
              <Badge colorScheme="red" variant="subtle">
                Error
              </Badge>
            </Flex>

            <VStack spacing={4} align="stretch">
              <Alert status="error">
                <AlertIcon />
                {testError?.message || evalTestError?.message || 'An error occurred'}
              </Alert>

              {/* Show partial results if any */}
              {results && results.data && (
                <Box>
                  <Text fontWeight="medium" mb={2} color="orange.700">
                    Partial Results Retrieved:
                  </Text>
                  <Box p={3} bg="orange.50" borderRadius="md" borderLeft="4px" borderLeftColor="orange.400">
                    <Text fontSize="sm">
                      Some data was retrieved before the error occurred. Check the results above.
                    </Text>
                  </Box>
                </Box>
              )}

              {/* Network Error Details */}
              {(testError?.networkError || evalTestError?.networkError) && (
                <Box>
                  <Text fontWeight="medium" mb={2} color="red.700">
                    Network Error Details:
                  </Text>
                  <Code 
                    p={4} 
                    borderRadius="md" 
                    display="block" 
                    whiteSpace="pre-wrap"
                    maxH="200px"
                    overflowY="auto"
                    bg="red.50"
                    borderColor="red.200"
                  >
                    {JSON.stringify(testError?.networkError || evalTestError?.networkError, null, 2)}
                  </Code>
                </Box>
              )}

              {/* GraphQL Errors */}
              {((testError?.graphQLErrors && testError.graphQLErrors.length > 0) || (evalTestError?.graphQLErrors && evalTestError.graphQLErrors.length > 0)) && (
                <Box>
                  <Text fontWeight="medium" mb={2} color="red.700">
                    GraphQL Errors:
                  </Text>
                  {(testError?.graphQLErrors || evalTestError?.graphQLErrors || []).map((error, index) => (
                    <Alert key={index} status="error" mb={2}>
                      <AlertIcon />
                      <Text fontSize="sm">{error.message}</Text>
                    </Alert>
                  ))}
                </Box>
              )}

              <Flex gap={3}>
                <Button onClick={resetTest} colorScheme="blue" flex={1}>
                  Try Again
                </Button>
                <Button 
                  onClick={() => {
                    console.error('Test Error:', testError || evalTestError);
                    alert('Error details logged to console');
                  }} 
                  variant="outline" 
                  colorScheme="red"
                >
                  Debug Error
                </Button>
              </Flex>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Enhanced Progress Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} closeOnOverlayClick={false} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex align="center">
              Scraping FISDAP Data
              {fetching && (
                <Badge ml={3} colorScheme="blue" variant="subtle">
                  In Progress
                </Badge>
              )}
            </Flex>
          </ModalHeader>
          {!fetching && <ModalCloseButton />}
          
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <Box p={4} bg="blue.50" borderRadius="md">
                <VStack align="start" spacing={2}>
                  <Text color="blue.800" fontWeight="medium">
                    Target Shift ID: <Code fontSize="sm">{shiftId}</Code>
                  </Text>
                  <Text color="blue.700" fontSize="sm">
                    Mode: <Badge colorScheme={bypassMode ? "blue" : "green"} size="sm">
                      {bypassMode ? "Test (No Save)" : "Full (Save to DB)"}
                    </Badge>
                  </Text>
                  {mockSubject && (
                    <Text color="blue.700" fontSize="sm">
                      Subject: <Code fontSize="xs">{mockSubject}</Code>
                    </Text>
                  )}
                </VStack>
              </Box>

              {fetching && (
                <VStack spacing={3}>
                  <Box w="full" bg="gray.100" borderRadius="full" h="3px" overflow="hidden">
                    <Box 
                      w="full" 
                      h="full" 
                      bg="blue.400" 
                      css={{
                        animation: 'pulse 2s ease-in-out infinite'
                      }}
                    />
                  </Box>
                  <Text textAlign="center" color="blue.600" fontSize="sm">
                    Extracting patient encounter data from FISDAP...
                  </Text>
                  <Text textAlign="center" color="gray.500" fontSize="xs">
                    This may take 30-60 seconds depending on shift complexity
                  </Text>
                </VStack>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Box p={4} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" color="gray.600">
          <strong>Note:</strong> This interface tests the FISDAP scraping functionality directly.
          Enter a valid FISDAP shift ID to extract patient encounter data.
        </Text>
      </Box>
    </Box>
  );
}